import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { ToastContext } from '../App';
import { formatMoney } from '../utils/helpers';

const TYPE_LABEL = { mfc: 'МФЦ', court: 'Судебное', unknown: '—' };

export default function ReferralPage() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const showToast = useContext(ToastContext);

  const load = () => {
    setLoading(true);
    api.get('/referrals').then(r => { setGroups(r.data); setLoading(false); }).catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const markPaid = async (clientId) => {
    try {
      await api.put(`/referrals/client/${clientId}/pay`);
      showToast('Отмечено');
      load();
    } catch {
      showToast('Ошибка', 'error');
    }
  };

  const totalClients = groups.reduce((s, g) => s + g.clients.length, 0);
  const totalCash = groups.reduce((s, g) => s + g.paid_cash, 0);
  const totalDiscount = groups.reduce((s, g) => s + g.paid_discount, 0);
  const pendingCash = groups.reduce((s, g) => s + (g.total_cash - g.paid_cash), 0);

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Загрузка...</div>;

  return (
    <div>
      <h1 className="page-title" style={{ marginBottom: 24 }}>Реферальная программа</h1>

      {/* Итоги */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, marginBottom: 24 }}>
        <div className="card" style={{ textAlign: 'center', padding: 20 }}>
          <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--accent)' }}>{totalClients}</div>
          <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Приведённых клиентов</div>
        </div>
        <div className="card" style={{ textAlign: 'center', padding: 20 }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--green)' }}>{formatMoney(totalCash)}</div>
          <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Выплачено деньгами</div>
          {pendingCash > 0 && (
            <div style={{ fontSize: 12, color: 'var(--yellow)', marginTop: 4 }}>ждёт: {formatMoney(pendingCash)}</div>
          )}
        </div>
        <div className="card" style={{ textAlign: 'center', padding: 20 }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>{formatMoney(totalDiscount)}</div>
          <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Скидок предоставлено</div>
        </div>
        <div className="card" style={{ textAlign: 'center', padding: 20 }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>
            {formatMoney(pendingCash)}
          </div>
          <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Ожидает выплаты</div>
        </div>
      </div>

      {groups.length === 0 ? (
        <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
          Нет данных. Укажите реферрала в карточке клиента.
        </div>
      ) : (
        groups.map((group, gi) => (
          <div key={gi} className="card" style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ width:8, height:8, borderRadius:'50%', background:'var(--accent)', display:'inline-block', flexShrink:0 }} />
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>
                    {group.referrer_client_id ? (
                      <span style={{ cursor: 'pointer', color: 'var(--accent)' }} onClick={() => navigate('/clients/' + group.referrer_client_id)}>
                        {group.referrer_name}
                      </span>
                    ) : (
                      <span>{group.referrer_name}</span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    {group.referrer_client_id ? 'клиент' : 'внешний реферрал'}
                    {' · '}привёл {group.clients.length} кл.
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 16, textAlign: 'right' }}>
                {group.total_cash > 0 && (
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--green)' }}>
                      {formatMoney(group.paid_cash)} / {formatMoney(group.total_cash)}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>деньги</div>
                  </div>
                )}
                {group.total_discount > 0 && (
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)' }}>
                      {formatMoney(group.paid_discount)} / {formatMoney(group.total_discount)}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>скидки</div>
                  </div>
                )}
              </div>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)', color: 'var(--text-muted)' }}>
                  <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600 }}>Клиент</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600 }}>Тип</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600 }}>Вознаграждение</th>
                  <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600 }}>Сумма</th>
                  <th style={{ textAlign: 'center', padding: '6px 8px', fontWeight: 600 }}>Статус</th>
                  <th style={{ padding: '6px 8px' }}></th>
                </tr>
              </thead>
              <tbody>
                {group.clients.map((c, ci) => (
                  <tr key={ci} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '8px 8px' }}>
                      <span style={{ cursor: 'pointer', color: 'var(--accent)', fontWeight: 500 }} onClick={() => navigate('/clients/' + c.id)}>
                        {c.name}
                      </span>
                    </td>
                    <td style={{ padding: '8px 8px', color: 'var(--text-muted)' }}>
                      {TYPE_LABEL[c.type] || c.type}
                    </td>
                    <td style={{ padding: '8px 8px' }}>
                      {c.fee_type === 'discount'
                        ? <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>скидка клиенту</span>
                        : <span style={{ color: 'var(--green)', fontSize: 12 }}>деньгами</span>}
                    </td>
                    <td style={{ padding: '8px 8px', textAlign: 'right', fontWeight: 600 }}>
                      {c.fee_amount > 0 ? formatMoney(c.fee_amount) : '—'}
                    </td>
                    <td style={{ padding: '8px 8px', textAlign: 'center' }}>
                      {c.fee_paid ? (
                        <span style={{ background: 'var(--green-bg)', color: 'var(--green)', borderRadius: 4, padding: '2px 8px', fontSize: 12 }}>
                          {c.fee_type === 'discount' ? 'скидка дана' : 'выплачено'}
                        </span>
                      ) : (
                        <span style={{ background: 'var(--yellow-bg)', color: 'var(--yellow)', borderRadius: 4, padding: '2px 8px', fontSize: 12 }}>
                          ожидает
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '8px 8px', textAlign: 'right' }}>
                      {!c.fee_paid && (
                        <button className="btn btn-secondary btn-sm" style={{ fontSize: 12 }} onClick={() => markPaid(c.id)}>
                          {c.fee_type === 'discount' ? 'Скидка дана' : 'Выплачено'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))
      )}
    </div>
  );
}
